export interface StatutoryPeriodSummary {
  period: string;
  depositsCollectedCents: number;
  depositsCollectedUsd: number;
  containersSold: number;
  depositsRedeemedCents: number;
  depositsRedeemedUsd: number;
  containersRedeemed: number;
  netLiabilityCents: number;
  netLiabilityUsd: number;
}

export interface StatutoryLedgerReport {
  generatedAt: string;
  statutoryJurisdiction: 'MICHIGAN_MCL_445_571';
  depositPerUnitCents: number;
  totals: {
    depositsCollectedCents: number;
    depositsCollectedUsd: number;
    containersSold: number;
    depositsRedeemedCents: number;
    depositsRedeemedUsd: number;
    containersRedeemed: number;
    netLiabilityCents: number;
    netLiabilityUsd: number;
  };
  periods: StatutoryPeriodSummary[];
}

import { z } from 'zod';

import { apiRequest, ApiError } from '../../../lib/api';
import { buildIdempotencyKey } from '../../../lib/idempotency';
import { validateWithSchema } from '../../shared/utils/apiValidation.util';
import { API_PATHS } from '../../../lib/apiRoutes';
import type {
  ApprovalSummary,
  BottleReturnFilters,
  ReturnReviewRequest,
  ReturnVerificationRecord,
  ReturnVerificationListResponse,
  UpcEligibility,
  UpcEligibilityResponse
} from '../types/bottleReturns.types';

const objectIdSchema = z.string().trim().regex(/^[a-fA-F0-9]{24}$/);
const upcSchema = z.string().trim().regex(/^\d{8,14}$/);
const idempotencyKeySchema = z.string().trim().min(8).max(128);
const resolveIdempotencyKey = (scope: string, provided?: string) => idempotencyKeySchema.parse(provided?.trim() || buildIdempotencyKey(scope));
const isoDateSchema = z.string().trim().refine((value) => !Number.isNaN(new Date(value).getTime()), {
  message: 'Invalid date'
});
const MAX_DATE_RANGE_DAYS = 366;
/** Mirrors server BOTTLE_RETURN_MAX_BOTTLES_PER_REQUEST default. */
export const MAX_BOTTLES_PER_RETURN = 500;

const filtersSchema = z.object({
  status: z.string().trim().toLowerCase().pipe(z.enum(['pending', 'approved', 'rejected'])).optional(),
  page: z.coerce.number().int().positive().max(10_000).optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  startDate: isoDateSchema.optional(),
  endDate: isoDateSchema.optional()
});
/** Mirrors server reviewBottleReturnRequestSchema (PATCH /v1/bottleReturns/:id/review). */
const returnReviewSchema = z
  .object({
    status: z.enum(['approved', 'rejected']),
    reviewNote: z.string().trim().min(5).max(500),
    finalAcceptedCount: z.number().int().min(0).max(MAX_BOTTLES_PER_RETURN).optional()
  })
  .strict();

const verificationRecordSchema = z.object({
  id: z.string().optional(),
  _id: z.string().optional(),
  userId: z.string().optional(),
  user: z.string().optional(),
  username: z.string().optional(),
  count: z.number().optional(),
  countMode: z.enum(['counted', 'uncounted']).optional(),
  numberOfBottles: z.number().optional(),
  creditAmount: z.number().optional(),
  proofImageUrl: z.string().optional(),
  imageProofUrl: z.string().optional(),
  reviewNote: z.string().optional(),
  status: z.enum(['pending', 'approved', 'rejected']),
  appealStatus: z.string().optional(),
  submittedAt: z.string().optional(),
  reviewedAt: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional()
});

const listVerificationsSchema = z.object({
  success: z.boolean(),
  data: z.array(verificationRecordSchema),
  pagination: z
    .object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number()
    })
    .optional(),
  traceId: z.string().optional()
});

const upcEligibilitySchema = z.object({
  eligible: z.boolean().optional(),
  name: z.string().optional(),
  containerType: z.string().optional(),
  sizeOz: z.number().optional(),
  reason: z.string().optional(),
  reasonCode: z.string().optional(),
  success: z.boolean().optional(),
  data: z
    .object({
      eligible: z.boolean().optional(),
      name: z.string().optional(),
      containerType: z.string().optional(),
      sizeOz: z.number().optional(),
      reason: z.string().optional(),
      reasonCode: z.string().optional()
    })
    .optional()
});

const approvalSummarySchema = z
  .object({
    id: z.string().optional(),
    _id: z.string().optional(),
    type: z.string(),
    status: z.enum(['pending', 'approved', 'rejected']),
    referenceId: z.string().or(z.object({ toString: z.function().args().returns(z.string()) })).optional(),
    reviewerNotes: z.string().optional(),
    createdAt: z.union([z.string(), z.coerce.date()]).optional(),
    updatedAt: z.union([z.string(), z.coerce.date()]).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    requestedBy: z.unknown().optional()
  })
  .passthrough();

const approvalsResponseSchema = z.object({
  success: z.boolean().optional(),
  approvals: z.array(approvalSummarySchema).optional(),
  data: z
    .union([
      z.array(approvalSummarySchema),
      z.object({ approvals: z.array(approvalSummarySchema).optional() }).passthrough()
    ])
    .optional(),
  traceId: z.string().optional()
});
const normalizeApproval = (approval: z.infer<typeof approvalSummarySchema>): ApprovalSummary & {
  metadata?: Record<string, unknown>;
  reviewerNotes?: string;
  referenceId?: string;
  requestedBy?: string;
} => {
  const createdAt =
    typeof approval.createdAt === 'string'
      ? approval.createdAt
      : approval.createdAt instanceof Date
        ? approval.createdAt.toISOString()
        : undefined;
  const updatedAt =
    typeof approval.updatedAt === 'string'
      ? approval.updatedAt
      : approval.updatedAt instanceof Date
        ? approval.updatedAt.toISOString()
        : undefined;
  return {
    id: approval.id ?? approval._id ?? '',
    type: approval.type,
    status: approval.status,
    userId: String(approval.requestedBy || 'system'),
    requestedBy: String(approval.requestedBy || 'system'),
    referenceId:
      typeof approval.referenceId === 'string'
        ? approval.referenceId
        : approval.referenceId?.toString?.() ?? '',
    ...(approval.reviewerNotes ? { reviewerNotes: approval.reviewerNotes } : {}),
    ...(createdAt ? { createdAt } : {}),
    ...(updatedAt ? { updatedAt } : {}),
    ...(approval.metadata ? { metadata: approval.metadata } : {})
  };
};

const normalizeUpc = (rawUpc: string): string => upcSchema.parse(String(rawUpc || '').trim());
const normalizeObjectId = (value: string): string => objectIdSchema.parse(String(value ?? '').trim());
const normalizeOptionalIsoDate = (value?: string): string | undefined => {
  if (!value) return undefined;
  const asDate = new Date(value);
  return Number.isNaN(asDate.getTime()) ? undefined : asDate.toISOString();
};

const normalizeNonNegativeNumber = (value: unknown): number => {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
};

const normalizeVerification = (record: z.infer<typeof verificationRecordSchema>): ReturnVerificationRecord => ({
  id: record.id ?? record._id ?? '',
  userId: record.userId ?? record.user ?? '',
  username: record.username?.trim() ?? '',
  count: normalizeNonNegativeNumber(record.count ?? record.numberOfBottles),
  countMode: record.countMode ?? 'counted',
  creditAmount: normalizeNonNegativeNumber(record.creditAmount),
  proofImageUrl: record.proofImageUrl ?? record.imageProofUrl ?? '',
  ...(record.reviewNote ? { reviewNote: record.reviewNote } : {}),
  ...(record.appealStatus ? { appealStatus: record.appealStatus } : {}),
  status: record.status,
  submittedAt:
    normalizeOptionalIsoDate(record.submittedAt) ??
    normalizeOptionalIsoDate(record.createdAt) ??
    new Date(0).toISOString(),
  reviewedAt: normalizeOptionalIsoDate(record.reviewedAt),
  createdAt:
    normalizeOptionalIsoDate(record.createdAt) ??
    normalizeOptionalIsoDate(record.submittedAt) ??
    new Date(0).toISOString(),
  updatedAt:
    normalizeOptionalIsoDate(record.updatedAt) ??
    normalizeOptionalIsoDate(record.createdAt) ??
    new Date(0).toISOString()
});

const toUpcEligibility = (payload: UpcEligibilityResponse): UpcEligibility => {
  const body = payload.data && typeof payload.data === 'object' ? payload.data : payload;
  const eligible = body.eligible === true;
  return {
    isEligible: eligible,
    ...(body.name ? { name: body.name } : {}),
    ...(body.containerType ? { containerType: body.containerType } : {}),
    ...(typeof body.sizeOz === 'number' ? { sizeOz: body.sizeOz } : {}),
    ...(body.reason ? { reason: body.reason } : {}),
    ...(body.reasonCode ? { reasonCode: body.reasonCode } : {}),
    checkedAt: new Date().toISOString()
  };
};

const buildQuery = (filters?: BottleReturnFilters): string => {
  if (!filters) return '';
  const parsed = filtersSchema.parse(filters);
  const normalizedStartDate = normalizeOptionalIsoDate(parsed.startDate);
  const normalizedEndDate = normalizeOptionalIsoDate(parsed.endDate);

  if (normalizedStartDate && normalizedEndDate && new Date(normalizedStartDate) > new Date(normalizedEndDate)) {
    throw new Error('startDate must be before or equal to endDate');
  }
  if (normalizedStartDate && normalizedEndDate) {
    const dayMs = 1000 * 60 * 60 * 24;
    const rangeDays = (new Date(normalizedEndDate).getTime() - new Date(normalizedStartDate).getTime()) / dayMs;
    if (rangeDays > MAX_DATE_RANGE_DAYS) {
      throw new Error(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`);
    }
  }
  const search = new URLSearchParams();
  if (parsed.status) search.set('status', parsed.status);
  if (parsed.page) search.set('page', String(parsed.page));
  if (parsed.limit) search.set('limit', String(parsed.limit));
  if (normalizedStartDate) search.set('startDate', normalizedStartDate);
  if (normalizedEndDate) search.set('endDate', normalizedEndDate);
  const query = search.toString();
  return query ? `?${query}` : '';
};

/** Feature API contract source of truth: docs/api/endpoints.md */
export const RETURNS_API_CONTRACT = {
  approvalsPending: API_PATHS.approvals.pending,
  approvalsList: `${API_PATHS.approvals.list}?limit=100`,
  listVerifications: API_PATHS.bottleReturns.root,
  myReturns: API_PATHS.bottleReturns.myReturns,
  appeal: API_PATHS.bottleReturns.appeal,
  reviewVerification: API_PATHS.bottleReturns.review,
  auditSettle: API_PATHS.bottleReturns.auditSettle,
  upcEligibility: API_PATHS.upc.eligibility,
  requiredPayloadKeys: {
    reviewVerification: ['status', 'reviewNote'],
    appeal: ['appealEvidenceUrl'],
    auditSettle: ['verifiedCount']
  }
} as const;

export const bottleReturnsApi = {
  approvals: async (): Promise<{ success: boolean; data: ApprovalSummary[]; approvals?: ApprovalSummary[]; traceId?: string }> => {
    const response = await apiRequest<z.infer<typeof approvalsResponseSchema>>(RETURNS_API_CONTRACT.approvalsList);
    const parsed = approvalsResponseSchema.parse(response);
    const nested =
      parsed.data && !Array.isArray(parsed.data) && Array.isArray(parsed.data.approvals)
        ? parsed.data.approvals
        : null;
    const source = Array.isArray(parsed.approvals)
      ? parsed.approvals
      : Array.isArray(parsed.data)
        ? parsed.data
        : nested ?? [];
    const data = source.map(normalizeApproval);
    return {
      success: parsed.success ?? true,
      data,
      approvals: data,
      ...(parsed.traceId ? { traceId: parsed.traceId } : {})
    };
  },
  listVerifications: async (filters?: BottleReturnFilters): Promise<ReturnVerificationListResponse> => {
    const endpoint = `${RETURNS_API_CONTRACT.listVerifications}${buildQuery(filters)}`;
    const response = await apiRequest<ReturnVerificationListResponse>(endpoint);
    const parsed = listVerificationsSchema.parse(response);
    return {
      ...parsed,
      success: parsed.success ?? true,
      data: parsed.data.map(normalizeVerification)
    } as ReturnVerificationListResponse;
  },
  myHistory: async (filters?: BottleReturnFilters): Promise<ReturnVerificationListResponse> => {
    const endpoint = `${RETURNS_API_CONTRACT.myReturns}${buildQuery(filters)}`;
    const response = await apiRequest<ReturnVerificationListResponse>(endpoint);
    const parsed = listVerificationsSchema.parse(response);
    return { ...parsed, success: parsed.success ?? true, data: parsed.data.map(normalizeVerification) } as ReturnVerificationListResponse;
  },
  reviewVerification: async (verificationId: string, payload: ReturnReviewRequest, idempotencyKey?: string) => {
    const parsedVerificationId = normalizeObjectId(verificationId);
    const endpoint = RETURNS_API_CONTRACT.reviewVerification(parsedVerificationId);
    const reviewPayload = validateWithSchema(returnReviewSchema, payload, endpoint);

    const response = await apiRequest<{ success?: boolean; data?: z.infer<typeof verificationRecordSchema>; traceId?: string }>(endpoint, {
      method: 'PATCH',
      idempotencyKey: resolveIdempotencyKey('bottle-return-review', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(reviewPayload)
    });
    if (!response?.success || !response.data) {
      throw new Error('Bottle return review was not confirmed. Try again.');
    }
    const reviewed = normalizeVerification(response.data);
    if (reviewed.status !== reviewPayload.status) {
      throw new Error('Bottle return review was not confirmed. Try again.');
    }
    return {
      success: true as const,
      data: reviewed,
      traceId: response.traceId
    };
  },
  submitAppeal: async (
    returnId: string,
    payload: { appealEvidenceUrl: string; appealNote?: string },
    idempotencyKey?: string
  ) => {
    const endpoint = RETURNS_API_CONTRACT.appeal(normalizeObjectId(returnId));
    const appealEvidenceUrl = z.string().trim().url().parse(payload.appealEvidenceUrl);
    const appealNote = payload.appealNote?.trim();
    const response = await apiRequest<{ success?: boolean; data?: z.infer<typeof verificationRecordSchema>; traceId?: string }>(endpoint, {
      method: 'POST',
      idempotencyKey: resolveIdempotencyKey('bottle-return-appeal', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify({
        appealEvidenceUrl,
        ...(appealNote ? { appealNote } : {})
      })
    });
    if (!response?.success || !response.data) {
      throw new Error('Appeal was not saved. Try again.');
    }
    return {
      success: true as const,
      data: normalizeVerification(response.data),
      traceId: response.traceId
    };
  },
  auditSettleBag: async (returnId: string, verifiedCount: number, auditNote?: string, idempotencyKey?: string) => {
    const parsedId = normalizeObjectId(returnId);
    const endpoint = RETURNS_API_CONTRACT.auditSettle(parsedId);
    const response = await apiRequest<{ success?: boolean; data?: z.infer<typeof verificationRecordSchema>; traceId?: string }>(endpoint, {
      method: 'POST',
      idempotencyKey: resolveIdempotencyKey('bottle-return-audit-settle', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify({
        verifiedCount,
        auditNote: auditNote || 'Hub physical audit and instant wallet settlement'
      })
    });
    if (!response?.success || !response.data) {
      throw new Error('Bottle return audit & settlement failed. Try again.');
    }
    return {
      success: true as const,
      data: normalizeVerification(response.data),
      traceId: response.traceId
    };
  },
  checkUpcEligibility: async (upc: string): Promise<UpcEligibility> => {
    const normalizedUpc = normalizeUpc(upc);
    const endpoint = RETURNS_API_CONTRACT.upcEligibility(normalizedUpc);

    try {
      const response = await apiRequest<UpcEligibilityResponse>(endpoint, { method: 'GET' });
      const parsed = validateWithSchema(upcEligibilitySchema, response, endpoint);
      return toUpcEligibility(parsed);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return { isEligible: false, checkedAt: new Date().toISOString() };
      }
      throw error;
    }
  },
  getStatutoryLedger: async (params?: { startDate?: string; endDate?: string }) => {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    const qs = query.toString();
    const endpoint = `${API_PATHS.bottleReturns.statutoryLedger}${qs ? `?${qs}` : ''}`;
    const response = await apiRequest<{ success: boolean; data: StatutoryLedgerReport; traceId?: string }>(endpoint, {
      method: 'GET'
    });
    return response.data;
  },
  getStatutoryCsvUrl: (params?: { startDate?: string; endDate?: string }): string => {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    const qs = query.toString();
    return `${API_PATHS.bottleReturns.statutoryLedgerCsv}${qs ? `?${qs}` : ''}`;
  }
};


